import { useCallback, useEffect, useMemo, useState } from "react";
import { useSearchParams } from "react-router-dom";
import {
  Alert,
  Button,
  Card,
  Col,
  DatePicker,
  Drawer,
  Empty,
  Form,
  Input,
  List,
  message,
  Row,
  Select,
  Skeleton,
  Space,
  Statistic,
  Tabs,
  Tag,
  Typography,
} from "antd";
import {
  BankOutlined,
  DollarOutlined,
  ExportOutlined,
  FileTextOutlined,
  LinkOutlined,
  PlusOutlined,
  PrinterOutlined,
  ReloadOutlined,
  RocketOutlined,
} from "@ant-design/icons";
import dayjs from "dayjs";
import { useAuth } from "contexts/AuthContext";
import { useWorkspace } from "../../contexts/WorkspaceContext";
import { CareShell } from "../../components/care/CareLayout";
import PaystubDetailsCard from "components/care/payroll/PaystubDetailsCard";
import { printPaystubDocument } from "components/care/payroll/paystubPrint";
import WidgetSectionTitle from "widgets/WidgetSectionTitle";
import paymentMethodsService from "services/paymentMethodsService";
import paymentTransfersService from "services/paymentTransfersService";
import { createWorkspace } from "services/workspaceService";
import { careService } from "services/careService";
import userPayrollService from "services/userPayrollService";
import workspacePayrollService from "services/workspacePayrollService";
import { readable } from "workspaces/workspaceUtils";
import { OLIVEEB_COLORS, workspaceButtonStyles, workspaceStyles } from "workspaces/colors";

const { Text } = Typography;
const { RangePicker } = DatePicker;
const MY_PAYMENTS_TAB_KEYS = ["workspace", "timesheets", "transactions", "methods", "paystubs", "documents"];
const DEFAULT_MY_PAYMENTS_TAB = "workspace";

const pageContent = (data) => (Array.isArray(data) ? data : data?.content || []);

const firstText = (...values) =>
  values.map((value) => String(value || "").trim()).find(Boolean) || "";

const personalPaymentWorkspaceStorageKey = (userId) =>
  `myPaymentsPersonalWorkspaceId:${userId || "anonymous"}`;

const isPersonalWorkspace = (workspace = {}) => {
  const accountType = String(workspace.accountType || "").toUpperCase();
  return (
    accountType === "PERSONAL" ||
    accountType === "INDIVIDUAL" ||
    (!workspace.organizationId && accountType !== "ORGANIZATION")
  );
};

const defaultWorkspaceName = (user = {}) => {
  const name = [user.firstName, user.lastName].filter(Boolean).join(" ").trim();
  return name ? `${name}'s payment workspace` : "My payment workspace";
};

const money = (value, currency = "USD") => {
  if (value === undefined || value === null || value === "") return "-";
  const number = Number(value);
  if (!Number.isFinite(number)) return String(value);
  return new Intl.NumberFormat(undefined, {
    style: "currency",
    currency: currency || "USD",
  }).format(number);
};

const dateText = (value) => {
  if (!value) return "-";
  const date = new Date(value);
  if (!Number.isFinite(date.getTime())) return "-";
  return new Intl.DateTimeFormat(undefined, {
    month: "short",
    day: "2-digit",
    year: "numeric",
    hour: "2-digit",
    minute: "2-digit",
  }).format(date);
};

const shortDateText = (value) => {
  if (!value) return "-";
  const date = new Date(value);
  if (!Number.isFinite(date.getTime())) return "-";
  return new Intl.DateTimeFormat("en-US", {
    month: "short",
    day: "numeric",
    year: "numeric",
  }).format(date);
};

const boolValue = (value) => value === true || String(value).toLowerCase() === "true";

const objectValue = (value) => (value && typeof value === "object" && !Array.isArray(value) ? value : {});

const arrayValue = (value) => (Array.isArray(value) ? value : []);

const connectStripeAccountId = (account) =>
  account?.stripeAccountId ||
  account?.stripe_account_id ||
  account?.stripeConnectAccountId ||
  account?.accountId ||
  account?.connectedAccountId ||
  "";

const isDefaultIncoming = (method) =>
  Boolean(
    method?.defaultIncomingPaymentMethod ||
      method?.isDefaultIncomingPaymentMethod ||
      method?.isDefaultForCurrency ||
      method?.isDefaultPaymentAccount,
  );

const payoutMethodId = (method) =>
  method?.id || method?.externalPayoutAccountId || method?.externalAccountId || "";

const maskedPayoutMethod = (method) => {
  const brand =
    method?.bankName ||
    method?.bank ||
    method?.institutionName ||
    method?.brand ||
    method?.accountHolderName ||
    method?.type ||
    "Payment method";
  const last4 = method?.last4 || method?.lastDigits || method?.last4Digits;
  return last4 ? `${brand} ending ${last4}` : brand;
};

const transactionTitle = (transaction) =>
  transaction?.description ||
  transaction?.paymentDescription ||
  transaction?.chargeItemName ||
  transaction?.stripeTransferId ||
  readable(transaction?.chargeItemCode, "Payout");

const transactionAmount = (transaction) =>
  transaction?.netPayout ??
  transaction?.payeeAmount ??
  transaction?.chargeableAmount ??
  transaction?.amount ??
  transaction?.totalAmount ??
  transaction?.netAmount;

const transactionDate = (transaction) =>
  transaction?.transferredAt || transaction?.createdAt || transaction?.updatedAt;

const transactionStatus = (transaction) =>
  firstText(transaction?.status, transaction?.paymentStatus, "Status");

const payrollPaymentAmount = (payment = {}) =>
  payment.netPay ?? payment.payeeAmount ?? payment.chargeableAmount ?? payment.amount ?? payment.netAmount ?? payment.grossPay;

const payrollPaymentId = (payment = {}) =>
  payment?.id || payment?.paymentId || payment?.payrollPaymentDetailsId || "";

const paymentStatusText = (record = {}) =>
  firstText(record.paymentStatus, record.paymentDetails?.paymentStatus, record.paymentDetails?.workspaceChargeStatus, record.status, "Status");

const paymentStatusColor = (statusValue) => {
  const status = String(statusValue || "").toUpperCase();
  if (["PAID", "PAYOUT_PAID", "PAYOUT_SUCCEEDED", "SUCCEEDED", "SUCCESS", "COMPLETED"].includes(status)) return "green";
  if (["SCHEDULED", "PAYOUT_SCHEDULED", "PROCESSING", "PAYOUT_PROCESSING", "PENDING", "IN_TRANSIT"].includes(status)) return "gold";
  if (["FAILED", "PAYOUT_FAILED", "CANCELLED", "CANCELED", "REQUIRES_ACTION"].includes(status)) return "red";
  return "blue";
};

const payrollPaymentBelongsToUser = (payment = {}, userId = "", email = "") => {
  const details = objectValue(payment.paymentDetails);
  const employee = objectValue(details.employee);
  const rowUserIds = [
    payment.userId,
    payment.payeeUserId,
    payment.employeeUserId,
    employee.userId,
    employee.id,
  ].map((value) => String(value || "").trim()).filter(Boolean);
  const rowEmails = [
    payment.userEmail,
    payment.staffEmail,
    payment.payeeEmail,
    employee.email,
  ].map((value) => String(value || "").trim().toLowerCase()).filter(Boolean);
  const normalizedUserId = String(userId || "").trim();
  const normalizedEmail = String(email || "").trim().toLowerCase();
  if (rowUserIds.length && normalizedUserId) return rowUserIds.includes(normalizedUserId);
  if (rowEmails.length && normalizedEmail) return rowEmails.includes(normalizedEmail);
  return true;
};

const payrollPaymentActualPayDateFor = (payment = {}) => {
  const details = objectValue(payment.paymentDetails);
  const workspaceCharge = objectValue(payment.workspaceCharge || details.workspaceCharge || details.charge || payment.charge);
  const stripeCharge = objectValue(payment.stripeCharge || details.stripeCharge || workspaceCharge.stripeCharge);
  return firstText(
    payment.paidAt,
    payment.paymentPaidAt,
    payment.paidDate,
    payment.paymentDate,
    payment.paymentConfirmedAt,
    payment.paymentCompletedAt,
    payment.chargePaidAt,
    payment.chargedAt,
    payment.capturedAt,
    payment.completedAt,
    payment.succeededAt,
    payment.processedAt,
    payment.confirmedAt,
    details.paidAt,
    details.paymentPaidAt,
    details.paidDate,
    details.paymentDate,
    details.paymentConfirmedAt,
    details.paymentCompletedAt,
    details.chargePaidAt,
    details.chargedAt,
    details.capturedAt,
    details.completedAt,
    details.succeededAt,
    details.processedAt,
    details.confirmedAt,
    workspaceCharge.paidAt,
    workspaceCharge.paymentDate,
    workspaceCharge.completedAt,
    workspaceCharge.succeededAt,
    workspaceCharge.processedAt,
    workspaceCharge.confirmedAt,
    workspaceCharge.chargedAt,
    workspaceCharge.capturedAt,
    stripeCharge.paidAt,
    stripeCharge.createdAt,
    stripeCharge.capturedAt,
  );
};

const payrollPaymentDisplayPayDateFor = (payment = {}) => {
  return payrollPaymentActualPayDateFor(payment);
};

const payrollPaymentPeriodFor = (payment = {}) => {
  const details = objectValue(payment.paymentDetails);
  const period = objectValue(details.payPeriod || payment.payPeriod || payment.paymentSchedule);
  return {
    start: firstText(period.periodStart, period.start, period.startsAt, payment.periodStart),
    end: firstText(period.periodEnd, period.end, period.endsAt, payment.periodEnd),
    payDate: payrollPaymentDisplayPayDateFor(payment),
    frequency: firstText(period.frequency, payment.paymentSchedule?.frequency),
  };
};

const isPaymentInWindow = (payment = {}, [start, end] = []) => {
  if (!start || !end) return true;
  const period = payrollPaymentPeriodFor(payment);
  const periodStart = period.start ? new Date(period.start).getTime() : null;
  const periodEnd = period.end ? new Date(period.end).getTime() : null;
  const windowStart = start.startOf("day").valueOf();
  const windowEnd = end.endOf("day").valueOf();
  if (Number.isFinite(periodStart) && Number.isFinite(periodEnd)) {
    return periodStart <= windowEnd && periodEnd >= windowStart;
  }
  const paymentDate = new Date(period.payDate || payment.confirmedAt || payment.initiatedAt || payment.createdAt || payment.updatedAt).getTime();
  return Number.isFinite(paymentDate) && paymentDate >= windowStart && paymentDate <= windowEnd;
};

const payrollPaymentYtdTotalsFor = (payment = {}) =>
  objectValue(payment.yearToDateTotals || objectValue(payment.paymentDetails).yearToDateTotals);

const payrollLineName = (item = {}) => String(item.name || item.code || item.itemCode || item.type || "").toUpperCase();
const WA_PFML_2026_TOTAL_PREMIUM_RATE = 0.0113;
const WA_PFML_2026_EMPLOYEE_SHARE = 0.7143;
const WA_PFML_2026_EMPLOYEE_RATE = WA_PFML_2026_TOTAL_PREMIUM_RATE * WA_PFML_2026_EMPLOYEE_SHARE;
const WA_PFML_2026_WAGE_BASE = 184500;
const roundMoney = (value = 0) => Math.round(Number(value || 0) * 100) / 100;

const isComputedPayrollTaxLine = (item = {}) => {
  const name = payrollLineName(item);
  return Boolean(item.computed)
    || name.includes("FEDERAL")
    || name.includes("MEDICARE")
    || name.includes("SOCIAL_SECURITY")
    || name.includes("SOCIAL SECURITY")
    || name.includes("FICA")
    || name.includes("PFML")
    || name.includes("PAID FAMILY")
    || name.includes("MEDICAL LEAVE")
    || name.includes("WORKERS")
    || name.includes("WORKER")
    || name.includes("COMPENSATION")
    || name.includes("L&I")
    || name.includes("LNI");
};

const payrollItemAmount = (item = {}, basis = 0) => {
  const directAmount = Number(item.amount ?? item.value);
  if (Number.isFinite(directAmount)) return directAmount;
  const rate = Number(item.rate ?? item.percentage ?? item.percent);
  if (!Number.isFinite(rate)) return 0;
  const normalizedRate = rate > 1 ? rate / 100 : rate;
  return roundMoney(Number(basis || 0) * normalizedRate);
};

const computedPayrollTaxLinesFor = (row = {}) => arrayValue(row.computedPayrollTaxes);

const computedPayrollTaxAmountFor = (row = {}) =>
  computedPayrollTaxLinesFor(row).reduce((sum, item) => sum + payrollItemAmount(item, row.grossPay), 0);

const regularPayAmountFor = (row = {}) =>
  String(row.payRateType || "HOURLY").toUpperCase() === "SALARY"
    ? Number(row.hourlyRate || 0)
    : Number(row.regularHours || 0) * Number(row.hourlyRate || 0);

const overtimePayAmountFor = (row = {}) => Number(row.overtimeHours || 0) * Number(row.overtimeRate || 0);

const hasAnyPayRate = (row = {}) =>
  Boolean(Number(row.hourlyRate || 0) || Number(row.overtimeRate || 0) || arrayValue(row.payRates).length);

const payRateDisplayParts = (row = {}) => {
  const rates = arrayValue(row.payRates);
  if (rates.length) {
    return rates.map((rate) => {
      const amount = Number(rate.amount || 0);
      if (!amount) return "Not set";
      const kind = readable(rate.rateKind || rate.kind || "Regular", "Regular");
      return String(rate.type || row.payRateType || "HOURLY").toUpperCase() === "SALARY"
        ? `${kind} - ${money(amount)} salary`
        : `${kind} - ${money(amount)} / hr`;
    });
  }
  return [row.hourlyRate ? `${money(row.hourlyRate)} / hr` : "Not set"];
};

const profileNameFor = (profile = {}) =>
  firstText(profile.name, profile.displayName, profile.fullName, profile.userProfile?.name, profile.user?.name);

const profileEmailFor = (profile = {}) =>
  firstText(profile.email, profile.userProfile?.email, profile.user?.email);

const profilePhoneFor = (profile = {}) =>
  firstText(profile.phone, profile.userProfile?.phone, profile.user?.phone);

const payrollPaymentToPaystubRow = (payment = {}) => {
  const details = objectValue(payment.paymentDetails);
  const employee = objectValue(details.employee);
  const hours = objectValue(details.hours);
  const totals = objectValue(details.totals);
  const payRates = objectValue(details.payRates);
  const deductions = arrayValue(details.deductions);
  const earnings = arrayValue(details.earnings);
  const regularRate = Number(payRates.regular ?? payment.regularRate ?? 0);
  const overtimeRate = Number(payRates.overtime ?? payment.overtimeRate ?? regularRate ?? 0);
  const payRateTypeValue = String(payRates.type || payment.payRateType || "HOURLY").toUpperCase();
  const regularHours = Number(hours.regular ?? payment.regularHours ?? 0);
  const overtimeHours = Number(hours.overtime ?? payment.overtimeHours ?? 0);
  const approvedHours = Number(hours.approved ?? payment.approvedHours ?? regularHours + overtimeHours);
  const grossPay = Number(payment.grossPay ?? totals.grossPay ?? 0);
  const netPay = Number(payment.netPay ?? totals.netPay ?? payrollPaymentAmount(payment) ?? 0);
  const federalTaxLine = deductions.find((item) => payrollLineName(item).includes("FEDERAL"));
  const computedTaxLines = deductions.filter((item) => !payrollLineName(item).includes("FEDERAL") && isComputedPayrollTaxLine(item));
  const fallbackWashingtonTaxLines = washingtonPayrollTaxesFromEmployerAddress({
    employerAddress: firstText(details.employer?.address, payment.workspaceAddress),
    grossPay,
    approvedHours,
    existingLines: deductions,
  }).filter((item) => !computedTaxLineExists(computedTaxLines, item));
  const nonFederalDeductions = deductions.filter((item) => !payrollLineName(item).includes("FEDERAL") && !isComputedPayrollTaxLine(item));
  const extraEarnings = earnings.filter((item) => !["REGULAR PAY", "OVERTIME PAY"].includes(payrollLineName(item)));
  return {
    paymentRecord: payment,
    profile: {
      id: payment.careProfileId,
      name: firstText(employee.name, payment.staffName),
      email: firstText(employee.email, payment.staffEmail, payment.userEmail),
      phone: employee.phone,
      userProfile: {
        id: payment.userId,
        name: firstText(employee.name, payment.staffName),
        email: firstText(employee.email, payment.staffEmail, payment.userEmail),
        phone: employee.phone,
      },
    },
    employeeAddress: employee.address,
    employer: objectValue(details.employer),
    payRates: [
      regularRate ? { amount: regularRate, rateKind: "REGULAR", type: payRateTypeValue, status: "ACTIVE" } : null,
      overtimeRate ? { amount: overtimeRate, rateKind: "OVERTIME", type: "HOURLY", status: "ACTIVE" } : null,
    ].filter(Boolean),
    timesheets: arrayValue(details.timesheets),
    approvedHours,
    regularHours,
    overtimeHours,
    hourlyRate: regularRate,
    overtimeRate,
    payRateType: payRateTypeValue,
    basePay: Number(totals.basePay ?? totals.regularPay ?? regularPayAmountFor({ regularHours, hourlyRate: regularRate, payRateType: payRateTypeValue })),
    activeEarnings: extraEarnings.map((item) => ({ ...item, amountType: "FIXED", scope: item.scope || "WORKSPACE" })),
    activeDeductions: nonFederalDeductions.map((item) => ({ ...item, amountType: "FIXED", scope: item.scope || "WORKSPACE" })),
    computedPayrollTaxes: [...computedTaxLines, ...fallbackWashingtonTaxLines].map((item) => ({
      ...item,
      amountType: "FIXED",
      scope: item.scope || "WORKSPACE",
    })),
    grossPay,
    taxablePay: Number(totals.taxablePay ?? Math.max(0, grossPay - Number(totals.preTaxDeductions || 0))),
    federalTax: {
      amount: Number(totals.federalTax ?? federalTaxLine?.amount ?? 0),
      method: federalTaxLine?.method || "",
    },
    preTaxDeductionsAmount: Number(totals.preTaxDeductions || 0),
    postTaxDeductionsAmount: Number(totals.postTaxDeductions ?? Math.max(0, Number(payment.totalDeductions || totals.totalDeductions || 0) - Number(totals.federalTax ?? federalTaxLine?.amount ?? 0))),
    estimatedPay: netPay,
    currency: payment.currency || details.currency || "USD",
  };
};

const isWashingtonAddress = (value) => {
  const text = String(value || "").trim().toUpperCase();
  return text === "WA" || text === "WASHINGTON" || /\bWA\b/.test(text) || /\bWASHINGTON\b/.test(text);
};

const computedTaxLineExists = (lines = [], nextLine = {}) =>
  lines.some((line) => {
    const lineName = payrollLineName(line);
    const nextName = payrollLineName(nextLine);
    return lineName === nextName || lineName.includes(nextName) || nextName.includes(lineName);
  });

const washingtonPayrollTaxesFromEmployerAddress = ({ employerAddress, grossPay = 0, approvedHours = 0, existingLines = [] } = {}) => {
  if (!isWashingtonAddress(employerAddress)) return [];
  const pay = Math.max(0, Number(grossPay || 0));
  const hours = Math.max(0, Number(approvedHours || 0));
  const fallbackLines = [
    {
      id: "WA_PFML_EE",
      code: "WA_PFML_EE",
      name: "WA Paid Family and Medical Leave EE",
      scope: "WORKSPACE",
      taxTreatment: "POST_TAX",
      amountType: "FIXED",
      amount: roundMoney(Math.min(pay, WA_PFML_2026_WAGE_BASE) * WA_PFML_2026_EMPLOYEE_RATE),
      method: "2026 WA PFML employee share: 71.43% of 1.13%, capped at the Social Security wage base.",
      computed: true,
      basis: pay,
      rate: WA_PFML_2026_EMPLOYEE_RATE,
    },
    {
      id: "WA_WORKERS_COMP_EE",
      code: "WA_WORKERS_COMP_EE",
      name: "WA Workers' Compensation Tax",
      scope: "WORKSPACE",
      taxTreatment: "POST_TAX",
      amountType: "FIXED",
      amount: 0,
      method: "WA L&I employee hourly rate is not configured for this saved payment.",
      computed: true,
      basis: hours,
      rate: 0,
    },
  ];
  return fallbackLines.filter((line) => !computedTaxLineExists(existingLines, line));
};

const payrollDocumentUrl = (document = {}) =>
  firstText(document.signedUrl, document.downloadUrl, document.previewUrl, document.url, document.fileUrl, document.file?.signedUrl, document.file?.downloadUrl, document.file?.url);

const payrollDocumentName = (document = {}, fallback = "Document") =>
  firstText(document.fileName, document.filename, document.name, document.originalFileName, document.title, document.file?.fileName, document.file?.name, fallback);

const payrollDocumentType = (document = {}) =>
  firstText(document.contentType, document.mimeType, document.type, document.fileType, document.file?.contentType, document.file?.mimeType);

const isPayrollImageDocument = (document = {}, url = "") => {
  const type = payrollDocumentType(document).toLowerCase();
  const name = payrollDocumentName(document, "").toLowerCase();
  return type.startsWith("image/") || /\.(png|jpe?g|gif|webp|bmp|svg|heic|heif)(\?|$)/i.test(url || name);
};

const payrollDocumentsFor = (payment = {}) => {
  const details = objectValue(payment.paymentDetails);
  const proofDocuments = arrayValue(payment.proofDocuments || details.proofDocuments).map((document, index) => ({
    ...(typeof document === "string" ? { fileId: document } : objectValue(document)),
    kind: "Proof",
    fallbackName: `Proof document ${index + 1}`,
  }));
  const signatureFileId = firstText(
    payment.acknowledgementSignatureFileId,
    details.acknowledgementSignatureFileId,
    details.signatureDocument?.fileId,
    details.signature?.fileId,
  );
  const signatureDocument = signatureFileId
    ? {
        ...objectValue(details.signatureDocument || details.signature),
        fileId: signatureFileId,
        kind: "Signature",
        fallbackName: "Employee signature",
      }
    : null;
  return [...proofDocuments, signatureDocument].filter(Boolean);
};

const rowEntries = (row = {}) => row.entries || row.timeEntries || [];

const entryStart = (entry = {}) => entry.startTimestamp || entry.startAt || entry.startDateTime || entry.startTime || "";

const entryEnd = (entry = {}) => entry.endTimestamp || entry.endAt || entry.endDateTime || entry.endTime || "";

const timestampMillis = (value) => {
  if (!value) return null;
  const time = new Date(value).getTime();
  return Number.isFinite(time) ? time : null;
};

const hoursBetween = (start, end) => {
  const startMs = timestampMillis(start);
  const endMs = timestampMillis(end);
  if (startMs === null || endMs === null) return 0;
  return Math.max(0, endMs - startMs) / 3600000;
};

const formatHours = (value) => {
  const hours = Number(value || 0);
  return `${hours.toFixed(2)} hr${Math.abs(hours - 1) < 0.005 ? "" : "s"}`;
};

const profileIdFor = (profile = {}) => profile.id || profile.careProfileId || "";

const normalizeCareProfile = (result) => {
  if (Array.isArray(result)) return result[0] || null;
  if (Array.isArray(result?.content)) return result.content[0] || null;
  if (Array.isArray(result?.data)) return result.data[0] || null;
  return result || null;
};

const timesheetDate = (row = {}) => {
  const entries = rowEntries(row);
  return firstText(
    row.workDate,
    row.date,
    row.startTimestamp,
    row.startAt,
    row.createdAt,
    entryStart(entries[0] || {}),
  );
};

const timesheetHours = (row = {}) => {
  const entries = rowEntries(row);
  if (row.totalHours !== undefined && row.totalHours !== null) return Number(row.totalHours || 0);
  if (row.hours !== undefined && row.hours !== null) return Number(row.hours || 0);
  if (!entries.length) return hoursBetween(row.startTimestamp || row.startAt, row.endTimestamp || row.endAt);
  return entries.reduce((sum, entry) => sum + Number(entry.hours ?? hoursBetween(entryStart(entry), entryEnd(entry))), 0);
};

const MyPaymentsPage = () => {
  const [searchParams, setSearchParams] = useSearchParams();
  const selectedTab = MY_PAYMENTS_TAB_KEYS.includes(searchParams.get("tab"))
    ? searchParams.get("tab")
    : DEFAULT_MY_PAYMENTS_TAB;
  const { user } = useAuth();
  const {
    workspaces,
    selectedWorkspaceId,
    workspaceListReady,
    refreshWorkspaces,
    loading: loadingWorkspaces,
  } = useWorkspace();
  const actorId = user?.id || user?.userId || "";
  const userEmail = firstText(user?.email, user?.profile?.email, user?.username);
  const [createWorkspaceForm] = Form.useForm();
  const [activeTab, setActiveTab] = useState(selectedTab);
  const [paystubRange, setPaystubRange] = useState([dayjs().startOf("month"), dayjs().endOf("month")]);
  const [linkedWorkspaceId, setLinkedWorkspaceId] = useState("");
  const [selectedLinkWorkspaceId, setSelectedLinkWorkspaceId] = useState("");
  const [loadingPayrollLink, setLoadingPayrollLink] = useState(false);
  const [transactions, setTransactions] = useState([]);
  const [payrollPayments, setPayrollPayments] = useState([]);
  const [selectedPayrollPayment, setSelectedPayrollPayment] = useState(null);
  const [approvedTimesheets, setApprovedTimesheets] = useState([]);
  const [incomingMethods, setIncomingMethods] = useState([]);
  const [connectAccount, setConnectAccount] = useState(null);
  const [loadingTransactions, setLoadingTransactions] = useState(false);
  const [loadingPayrollPayments, setLoadingPayrollPayments] = useState(false);
  const [loadingPayrollPaymentDetail, setLoadingPayrollPaymentDetail] = useState("");
  const [loadingTimesheets, setLoadingTimesheets] = useState(false);
  const [loadingMethods, setLoadingMethods] = useState(false);
  const [linkingWorkspace, setLinkingWorkspace] = useState(false);
  const [createWorkspaceOpen, setCreateWorkspaceOpen] = useState(false);
  const [creatingWorkspace, setCreatingWorkspace] = useState(false);
  const [openingStripe, setOpeningStripe] = useState(false);

  const linkedWorkspace = useMemo(
    () => workspaces.find((workspace) => workspace?.id === linkedWorkspaceId) || null,
    [linkedWorkspaceId, workspaces],
  );

  const workspaceOptions = useMemo(
    () =>
      workspaces
        .filter((workspace) => workspace?.id && isPersonalWorkspace(workspace))
        .map((workspace) => ({
          value: workspace.id,
          label: workspace.name || workspace.workspaceName || "Workspace",
          workspace,
        })),
    [workspaces],
  );

  const stripeAccountId = connectStripeAccountId(connectAccount);
  const isOnboarded = boolValue(connectAccount?.onboarded);
  const payoutsEnabled = boolValue(connectAccount?.payoutsEnabled ?? connectAccount?.payouts_enabled);
  const totalReceived = useMemo(
    () => transactions.reduce((sum, item) => sum + Number(transactionAmount(item) || 0), 0),
    [transactions],
  );
  const approvedTimesheetHours = useMemo(
    () => approvedTimesheets.reduce((sum, item) => sum + timesheetHours(item), 0),
    [approvedTimesheets],
  );
  const currentUserPayrollPayments = useMemo(
    () => payrollPayments.filter((payment) => payrollPaymentBelongsToUser(payment, actorId, userEmail)),
    [actorId, payrollPayments, userEmail],
  );
  const filteredCurrentUserPayrollPayments = useMemo(
    () => currentUserPayrollPayments.filter((payment) => isPaymentInWindow(payment, paystubRange)),
    [currentUserPayrollPayments, paystubRange],
  );
  const payrollPaymentTotal = useMemo(
    () => filteredCurrentUserPayrollPayments.reduce((sum, item) => sum + Number(payrollPaymentAmount(item) || 0), 0),
    [filteredCurrentUserPayrollPayments],
  );

  useEffect(() => {
    if (activeTab !== selectedTab) {
      setActiveTab(selectedTab);
    }
  }, [activeTab, selectedTab]);

  const changeTab = (nextTab) => {
    setActiveTab(nextTab);
    setSearchParams((currentParams) => {
      const nextParams = new URLSearchParams(currentParams);
      if (nextTab === DEFAULT_MY_PAYMENTS_TAB) {
        nextParams.delete("tab");
      } else {
        nextParams.set("tab", nextTab);
      }
      return nextParams;
    });
  };

  useEffect(() => {
    const selectedId = payrollPaymentId(selectedPayrollPayment);
    if (selectedId && !filteredCurrentUserPayrollPayments.some((payment) => payrollPaymentId(payment) === selectedId)) {
      setSelectedPayrollPayment(null);
    }
  }, [filteredCurrentUserPayrollPayments, selectedPayrollPayment]);

  useEffect(() => {
    if (!actorId) return;
    let active = true;
    setLinkedWorkspaceId("");
    setSelectedLinkWorkspaceId("");
    setLoadingPayrollLink(true);
    userPayrollService.getForUser({ actorId, userId: actorId })
      .then((payroll) => {
        if (!active) return;
        const payrollWorkspaceId = firstText(payroll?.workspaceId);
        if (payrollWorkspaceId) {
          setLinkedWorkspaceId(payrollWorkspaceId);
          setSelectedLinkWorkspaceId(payrollWorkspaceId);
          localStorage.setItem(personalPaymentWorkspaceStorageKey(actorId), payrollWorkspaceId);
        }
      })
      .catch(() => null)
      .finally(() => {
        if (active) setLoadingPayrollLink(false);
      });
    return () => {
      active = false;
    };
  }, [actorId]);

  useEffect(() => {
    if (!linkedWorkspaceId && workspaceOptions.length === 1) {
      setSelectedLinkWorkspaceId(workspaceOptions[0].value);
    }
  }, [linkedWorkspaceId, workspaceOptions]);

  useEffect(() => {
    if (linkedWorkspaceId && workspaceOptions.length && !workspaceOptions.some((option) => option.value === linkedWorkspaceId)) {
      setLinkedWorkspaceId("");
      setSelectedLinkWorkspaceId("");
    }
  }, [linkedWorkspaceId, workspaceOptions]);

  const loadTransactions = useCallback(async () => {
    if (!actorId || !linkedWorkspaceId) {
      setTransactions([]);
      return;
    }
    try {
      setLoadingTransactions(true);
      const result = await paymentTransfersService.received.listForUser({
        actorId,
        userId: actorId,
        workspaceId: linkedWorkspaceId,
        page: 0,
        size: 20,
      });
      setTransactions(pageContent(result));
    } catch (error) {
      message.error(error?.response?.data?.message || error?.message || "Unable to load recent payment transfers.");
      setTransactions([]);
    } finally {
      setLoadingTransactions(false);
    }
  }, [actorId, linkedWorkspaceId]);

  const loadIncomingMethods = useCallback(async () => {
    if (!actorId || !linkedWorkspaceId) {
      setIncomingMethods([]);
      setConnectAccount(null);
      return;
    }
    try {
      setLoadingMethods(true);
      const [methods, account] = await Promise.all([
        paymentMethodsService.incoming.list({ actorId, userId: actorId, workspaceId: linkedWorkspaceId }).catch(() => []),
        paymentMethodsService.incoming.getConnectAccount({ actorId, userId: actorId, workspaceId: linkedWorkspaceId, refresh: false }).catch(() => null),
      ]);
      setIncomingMethods(methods);
      setConnectAccount(account);
    } catch (error) {
      message.error(error?.message || "Unable to load incoming payment methods.");
      setIncomingMethods([]);
      setConnectAccount(null);
    } finally {
      setLoadingMethods(false);
    }
  }, [actorId, linkedWorkspaceId]);

  const loadApprovedTimesheets = useCallback(async () => {
    if (!actorId || !workspaceListReady || !selectedWorkspaceId) {
      setApprovedTimesheets([]);
      return;
    }
    try {
      setLoadingTimesheets(true);
      const profile = normalizeCareProfile(await careService.careProfiles.getMine({
        actorId,
        workspaceId: selectedWorkspaceId,
        wid: selectedWorkspaceId,
      }));
      const careProfileId = profileIdFor(profile || {});
      if (!careProfileId) {
        setApprovedTimesheets([]);
        return;
      }
      const rows = await careService.timesheets.listForCareProfile(careProfileId, {
        actorId,
        workspaceId: selectedWorkspaceId,
        wid: selectedWorkspaceId,
        status: "APPROVED",
      });
      setApprovedTimesheets(Array.isArray(rows) ? rows : pageContent(rows));
    } catch (error) {
      message.error(error?.message || "Unable to load approved timesheets.");
      setApprovedTimesheets([]);
    } finally {
      setLoadingTimesheets(false);
    }
  }, [actorId, selectedWorkspaceId, workspaceListReady]);

  const loadPayrollPayments = useCallback(async () => {
    if (!actorId || !workspaceListReady || !selectedWorkspaceId) {
      setPayrollPayments([]);
      return;
    }
    try {
      setLoadingPayrollPayments(true);
      const result = await workspacePayrollService.listPayments({
        actorId,
        workspaceId: selectedWorkspaceId,
        userId: actorId,
      });
      setPayrollPayments(pageContent(result));
    } catch (error) {
      message.error(error?.response?.data?.message || error?.message || "Unable to load paystubs.");
      setPayrollPayments([]);
    } finally {
      setLoadingPayrollPayments(false);
    }
  }, [actorId, selectedWorkspaceId, workspaceListReady]);

  useEffect(() => {
    loadTransactions();
    loadIncomingMethods();
    loadApprovedTimesheets();
    loadPayrollPayments();
  }, [loadApprovedTimesheets, loadIncomingMethods, loadPayrollPayments, loadTransactions]);

  const linkPersonalWorkspace = async () => {
    if (!actorId || !selectedLinkWorkspaceId) {
      message.warning("Select a workspace to link.");
      return;
    }

    try {
      setLinkingWorkspace(true);
      await userPayrollService.linkWorkspace({ actorId, userId: actorId, workspaceId: selectedLinkWorkspaceId });
      localStorage.setItem(personalPaymentWorkspaceStorageKey(actorId), selectedLinkWorkspaceId);
      setLinkedWorkspaceId(selectedLinkWorkspaceId);
      message.success("Personal workspace linked for payments.");
    } catch (error) {
      localStorage.setItem(personalPaymentWorkspaceStorageKey(actorId), selectedLinkWorkspaceId);
      setLinkedWorkspaceId(selectedLinkWorkspaceId);
      message.warning("Linked in this browser. We could not update your profile record right now.");
    } finally {
      setLinkingWorkspace(false);
    }
  };

  const openCreateWorkspaceDrawer = () => {
    createWorkspaceForm.setFieldsValue({
      name: defaultWorkspaceName(user),
      description: "Personal workspace for payroll payments, payment methods, and payouts.",
    });
    setCreateWorkspaceOpen(true);
  };

  const createPersonalWorkspace = async (values) => {
    const name = firstText(values.name);
    if (!actorId || !name) {
      message.warning("Enter a workspace name.");
      return;
    }

    try {
      setCreatingWorkspace(true);
      const workspace = await createWorkspace({
        actorId,
        name,
        description: firstText(values.description),
        accountType: "PERSONAL",
        workspaceUse: "JOB_SEARCH",
      });
      await refreshWorkspaces();
      if (workspace?.id) {
        setSelectedLinkWorkspaceId(workspace.id);
        localStorage.setItem(personalPaymentWorkspaceStorageKey(actorId), workspace.id);
        setLinkedWorkspaceId(workspace.id);
        await userPayrollService.linkWorkspace({ actorId, userId: actorId, workspaceId: workspace.id }).catch(() => null);
      }
      createWorkspaceForm.resetFields();
      setCreateWorkspaceOpen(false);
      message.success("Personal workspace created and linked.");
    } catch (error) {
      message.error(error?.message || "Unable to create personal workspace.");
    } finally {
      setCreatingWorkspace(false);
    }
  };

  const openStripeUrl = (url) => {
    if (!url) {
      message.warning("Stripe link is not available right now.");
      return;
    }
    window.open(url, "_blank", "noopener,noreferrer");
  };

  const completeOnboarding = async () => {
    try {
      setOpeningStripe(true);
      const result = await paymentMethodsService.incoming.createOnboardingLink({
        actorId,
        userId: actorId,
        workspaceId: linkedWorkspaceId,
      });
      openStripeUrl(result?.url || result);
    } catch (error) {
      message.error(error?.message || "We could not start Stripe onboarding right now.");
    } finally {
      setOpeningStripe(false);
    }
  };

  const openDashboard = async () => {
    try {
      setOpeningStripe(true);
      const result = await paymentMethodsService.incoming.createDashboardLink({
        actorId,
        userId: actorId,
        workspaceId: linkedWorkspaceId,
      });
      openStripeUrl(result?.url || result);
    } catch (error) {
      message.error(error?.message || "We could not open the Stripe dashboard right now.");
    } finally {
      setOpeningStripe(false);
    }
  };

  const openPayrollPaymentDetails = async (payment) => {
    const paymentId = payrollPaymentId(payment);
    const workspaceId = payment?.workspaceId || selectedWorkspaceId;
    if (!actorId || !workspaceId || !paymentId) {
      message.warning("Unable to find the selected paystub.");
      return;
    }
    try {
      setLoadingPayrollPaymentDetail(paymentId);
      const detail = await workspacePayrollService.getPayment({ actorId, workspaceId, paymentId });
      setSelectedPayrollPayment(detail || payment);
    } catch (error) {
      message.error(error?.response?.data?.message || error?.message || "Unable to load paystub details.");
    } finally {
      setLoadingPayrollPaymentDetail("");
    }
  };

  const printPaystub = () => {
    if (!selectedPayrollPayment) {
      message.error("No paystub is ready to print.");
      return;
    }
    const payment = selectedPayrollPayment;
    const details = objectValue(payment.paymentDetails);
    const employer = objectValue(details.employer);
    const period = payrollPaymentPeriodFor(payment);
    const paystubRow = payrollPaymentToPaystubRow(payment);
    const staffName = firstText(profileNameFor(paystubRow.profile), user?.name, userEmail, "Employee");
    const staffEmail = firstText(profileEmailFor(paystubRow.profile), userEmail);
    const printed = printPaystubDocument({
      payment,
      row: paystubRow,
      period: {
        start: shortDateText(period.start),
        end: shortDateText(period.end),
        payDate: period.payDate ? shortDateText(period.payDate) : "",
        frequency: period.frequency,
      },
      employer,
      ytdTotals: payrollPaymentYtdTotalsFor(payment),
      staffName,
      staffEmail,
      staffPhone: profilePhoneFor(paystubRow.profile),
      employeeAddress: paystubRow.employeeAddress,
      employerName: firstText(employer.name, payment.workspaceName, "Organization"),
      employerAddress: firstText(employer.address, payment.workspaceAddress),
      employerContact: [employer.email, employer.phone].map((value) => String(value || "").trim()).filter(Boolean).join(" | "),
      timesheetCount: (paystubRow.timesheets || []).length,
      money,
      formatHours,
      readable,
      payRateDisplayParts,
      computedPayrollTaxLinesFor,
      computedPayrollTaxAmountFor,
      hasAnyPayRate,
      regularPayAmountFor,
      overtimePayAmountFor,
      payrollItemAmount,
    });
    if (!printed) {
      message.error("Allow pop-ups to print this paystub.");
    }
  };

  return (
    <CareShell className="dashboard-content project-page my-payments-page">
      <div className="d-flex justify-content-between align-items-center mb-3 mt-3 gap-3 flex-wrap">
        <WidgetSectionTitle
          icon="wallet"
          title="My Payments"
          subtitle="Review payroll payments, incoming payment methods, and payroll documents."
          className="flex-1"
          titleStyle={{ color: OLIVEEB_COLORS.oliveebDarkGreen }}
          subtitleStyle={{ color: OLIVEEB_COLORS.oliveebGreen }}
        />
        <Button
          icon={<ReloadOutlined />}
          style={workspaceButtonStyles.refresh}
          onClick={() => {
            loadTransactions();
            loadIncomingMethods();
            loadApprovedTimesheets();
            loadPayrollPayments();
          }}
        >
          Refresh
        </Button>
      </div>

      <Row gutter={[12, 12]} style={{ marginBottom: 16 }}>
        <Col xs={24} md={6}>
          <Card style={workspaceStyles.softCard}>
            <Statistic title="Paystubs" value={filteredCurrentUserPayrollPayments.length} prefix={<FileTextOutlined />} />
          </Card>
        </Col>
        <Col xs={24} md={6}>
          <Card style={workspaceStyles.softCard}>
            <Statistic title="Received total" value={money(totalReceived, transactions[0]?.currency || "USD")} />
          </Card>
        </Col>
        <Col xs={24} md={6}>
          <Card style={workspaceStyles.softCard}>
            <Statistic title="Approved Hours" value={formatHours(approvedTimesheetHours)} />
          </Card>
        </Col>
        <Col xs={24} md={6}>
          <Card style={workspaceStyles.softCard}>
            <Statistic title="Net paid" value={money(payrollPaymentTotal, filteredCurrentUserPayrollPayments[0]?.currency || "USD")} prefix={<DollarOutlined />} />
          </Card>
        </Col>
      </Row>

      <Tabs
        className="my-payments-tabs"
        activeKey={activeTab}
        onChange={changeTab}
        items={[
          { key: "workspace", label: "Personal Workspace" },
          { key: "timesheets", label: "Approved Timesheets" },
          { key: "transactions", label: "Received Payments" },
          { key: "methods", label: "Payment Methods" },
          { key: "paystubs", label: "Paystubs" },
          { key: "documents", label: "Documents" },
        ]}
      />

      {activeTab === "workspace" ? (
        <Card title={<Text strong style={{ color: OLIVEEB_COLORS.oliveebGreen }}>Personal Workspace Details</Text>}>
          <Space direction="vertical" size={16} style={{ width: "100%" }}>
            <Alert
              type={linkedWorkspace ? "success" : "info"}
              showIcon
              message={linkedWorkspace ? "Personal workspace linked" : "Link a personal workspace"}
              description={
                linkedWorkspace
                  ? "Payment methods, payroll transactions, and payouts will use this workspace."
                  : "Choose the workspace connected to your email so payroll payments can be managed in the right place."
              }
            />

            <Row gutter={[12, 12]}>
              <Col xs={24} md={8}>
                <Card size="small" style={{ borderRadius: 8 }}>
                  <Statistic title="Account email" value={userEmail || "Not available"} />
                </Card>
              </Col>
              <Col xs={24} md={8}>
                <Card size="small" style={{ borderRadius: 8 }}>
                  <Statistic title="Linked workspace" value={linkedWorkspace?.name || "Not linked"} />
                </Card>
              </Col>
              <Col xs={24} md={8}>
                <Card size="small" style={{ borderRadius: 8 }}>
                  <Statistic title="Personal workspaces" value={workspaceOptions.length} />
                </Card>
              </Col>
            </Row>

            <Card size="small" style={{ borderRadius: 8 }}>
              <Space direction="vertical" size={10} style={{ width: "100%" }}>
                <div className="d-flex justify-content-between align-items-center gap-2 flex-wrap">
                  <div>
                    <Text strong>Select workspace to link</Text>
                    <Text type="secondary" style={{ display: "block", fontSize: 12 }}>
                      Only personal workspaces can be linked for My Payments.
                    </Text>
                  </div>
                  <Button icon={<PlusOutlined />} style={workspaceButtonStyles.refresh} onClick={openCreateWorkspaceDrawer}>
                    Create Personal Workspace
                  </Button>
                </div>
                <Space.Compact style={{ width: "100%" }}>
                  <Select
                    showSearch
                    value={selectedLinkWorkspaceId || undefined}
                    placeholder="Select a personal workspace"
                    loading={loadingWorkspaces || loadingPayrollLink}
                    options={workspaceOptions}
                    optionFilterProp="label"
                    style={{ width: "100%" }}
                    onChange={setSelectedLinkWorkspaceId}
                    notFoundContent="No personal workspaces found"
                  />
                  <Button
                    type="primary"
                    icon={<LinkOutlined />}
                    loading={linkingWorkspace}
                    disabled={!selectedLinkWorkspaceId}
                    onClick={linkPersonalWorkspace}
                  >
                    {linkedWorkspace ? "Update Link" : "Link Workspace"}
                  </Button>
                </Space.Compact>
                <Button type="link" onClick={refreshWorkspaces} style={{ paddingLeft: 0 }}>
                  Refresh workspace list
                </Button>
              </Space>
            </Card>
          </Space>
        </Card>
      ) : null}

      {activeTab === "timesheets" ? (
        <Card title={<Text strong style={{ color: OLIVEEB_COLORS.oliveebGreen }}>Approved Timesheets</Text>}>
          {!selectedWorkspaceId ? (
            <Empty image={Empty.PRESENTED_IMAGE_SIMPLE} description="Select a care workspace first to view approved timesheets." />
          ) : loadingTimesheets ? (
            <Skeleton active paragraph={{ rows: 5 }} />
          ) : approvedTimesheets.length ? (
            <Space direction="vertical" size={12} style={{ width: "100%" }}>
              <Row gutter={[12, 12]}>
                <Col xs={24} md={12}>
                  <Card size="small" style={{ borderRadius: 8 }}>
                    <Statistic title="Approved timesheets" value={approvedTimesheets.length} />
                  </Card>
                </Col>
                <Col xs={24} md={12}>
                  <Card size="small" style={{ borderRadius: 8 }}>
                    <Statistic title="Approved hours" value={formatHours(approvedTimesheetHours)} />
                  </Card>
                </Col>
              </Row>
              <List
                dataSource={approvedTimesheets}
                renderItem={(item) => (
                  <List.Item key={item.id || `${timesheetDate(item)}-${timesheetHours(item)}`}>
                    <div className="my-payments-list-row">
                      <div>
                        <Text strong>{dateText(timesheetDate(item))}</Text>
                        <Space wrap size={[4, 4]} style={{ marginTop: 5 }}>
                          <Tag color="green">Approved</Tag>
                          <Tag color="blue">{readable(item.payCode || item.shiftType || "REGULAR")}</Tag>
                          {item.approvedAt ? <Tag>{`Approved ${dateText(item.approvedAt)}`}</Tag> : null}
                        </Space>
                      </div>
                      <Text strong style={{ color: OLIVEEB_COLORS.oliveebBlue }}>
                        {formatHours(timesheetHours(item))}
                      </Text>
                    </div>
                  </List.Item>
                )}
              />
            </Space>
          ) : (
            <Empty image={Empty.PRESENTED_IMAGE_SIMPLE} description="No approved timesheets found." />
          )}
        </Card>
      ) : null}

      {activeTab === "transactions" ? (
        <Card title={<Text strong style={{ color: OLIVEEB_COLORS.oliveebGreen }}>Received Payments</Text>}>
          {!linkedWorkspaceId ? (
            <Empty image={Empty.PRESENTED_IMAGE_SIMPLE} description="Link a personal workspace first to view received payouts." />
          ) : loadingTransactions ? (
            <Skeleton active paragraph={{ rows: 5 }} />
          ) : transactions.length ? (
            <List
              dataSource={transactions}
              renderItem={(item) => (
                <List.Item key={item.id || item.createdAt}>
                  <div className="my-payments-list-row">
                    <div>
                      <Text strong>{transactionTitle(item)}</Text>
                      <Space wrap size={[4, 4]} style={{ marginTop: 5 }}>
                        <Tag color="blue">{readable(transactionStatus(item), "Status")}</Tag>
                        {item.stripeTransferId ? <Tag color="cyan">{item.stripeTransferId}</Tag> : null}
                        {item.totalCharges ? <Tag color="gold">{`Fees ${money(item.totalCharges, item.currency)}`}</Tag> : null}
                        <Tag>{dateText(transactionDate(item))}</Tag>
                      </Space>
                    </div>
                    <Text strong style={{ color: OLIVEEB_COLORS.oliveebBlue }}>
                      {money(transactionAmount(item), item.currency)}
                    </Text>
                  </div>
                </List.Item>
              )}
            />
          ) : (
            <Empty image={Empty.PRESENTED_IMAGE_SIMPLE} description="No received payouts found." />
          )}
        </Card>
      ) : null}

      {activeTab === "methods" ? (
        <Space direction="vertical" size={16} style={{ width: "100%" }}>
          {!linkedWorkspaceId ? (
            <Alert
              type="info"
              showIcon
              message="Personal workspace required"
              description="Link a personal workspace first so incoming payment methods can be loaded from the right workspace."
            />
          ) : (
            <Alert
              type={stripeAccountId && isOnboarded ? "success" : "info"}
              showIcon
              message={stripeAccountId ? "Stripe account connected" : "Set up incoming payments"}
              description={
                stripeAccountId
                  ? `Payouts are ${payoutsEnabled ? "enabled" : "not enabled yet"}.`
                  : "Complete Stripe onboarding to receive payroll payouts."
              }
              action={
                <Button
                  type="primary"
                  icon={isOnboarded ? <ExportOutlined /> : <RocketOutlined />}
                  loading={openingStripe}
                  onClick={isOnboarded ? openDashboard : completeOnboarding}
                >
                  {isOnboarded ? "Stripe Dashboard" : "Start Onboarding"}
                </Button>
              }
            />
          )}
          <Card title={<Text strong style={{ color: OLIVEEB_COLORS.oliveebGreen }}>Incoming Payment Methods</Text>}>
            {!linkedWorkspaceId ? (
              <Empty image={Empty.PRESENTED_IMAGE_SIMPLE} description="Link a personal workspace first to view incoming payment methods." />
            ) : loadingMethods ? (
              <Skeleton active paragraph={{ rows: 4 }} />
            ) : incomingMethods.length ? (
              <List
                dataSource={incomingMethods}
                renderItem={(method) => {
                  const isDefault = isDefaultIncoming(method);
                  return (
                    <List.Item key={payoutMethodId(method)}>
                      <Card size="small" style={{ width: "100%", borderRadius: 8 }}>
                        <div className="my-payments-list-row">
                          <Space align="start">
                            <BankOutlined style={{ color: OLIVEEB_COLORS.oliveebGreen, marginTop: 4 }} />
                            <div>
                              <Text strong>{maskedPayoutMethod(method)}</Text>
                              <Space wrap size={[4, 4]} style={{ marginTop: 5 }}>
                                <Tag color="blue">Incoming</Tag>
                                {method.currency ? <Tag color="cyan">{String(method.currency).toUpperCase()}</Tag> : null}
                                {method.country ? <Tag color="geekblue">{method.country}</Tag> : null}
                                {isDefault ? <Tag color="green">Default</Tag> : null}
                              </Space>
                            </div>
                          </Space>
                          <Tag color={isDefault ? "green" : "default"}>{readable(method.status, "Status")}</Tag>
                        </div>
                      </Card>
                    </List.Item>
                  );
                }}
              />
            ) : (
              <Empty image={Empty.PRESENTED_IMAGE_SIMPLE} description="No incoming payment methods found." />
            )}
          </Card>
        </Space>
      ) : null}

      {activeTab === "paystubs" ? (
        <Space direction="vertical" size={14} style={{ width: "100%" }}>
          <Card
            className="payroll-pay-window-card"
            title={<Text strong className="payroll-section-title"><FileTextOutlined /> Paystubs</Text>}
          >
            <div className="payroll-paystub-controls">
              <label>
                <span>Pay period range</span>
                <RangePicker
                  value={paystubRange}
                  allowClear={false}
                  onChange={(nextRange) => setPaystubRange(nextRange || [dayjs().startOf("month"), dayjs().endOf("month")])}
                />
              </label>
            </div>
          </Card>
          <Card className="payroll-pay-staff-card" title={<Text strong className="payroll-section-title">Issued Paystubs</Text>}>
            {!selectedWorkspaceId ? (
              <Empty image={Empty.PRESENTED_IMAGE_SIMPLE} description="Select a care workspace first to view issued paystubs." />
            ) : loadingPayrollPayments ? (
              <Skeleton active paragraph={{ rows: 5 }} />
            ) : filteredCurrentUserPayrollPayments.length ? (
              <List
                className="payroll-pay-staff-list"
                dataSource={filteredCurrentUserPayrollPayments}
                renderItem={(payment) => {
                  const period = payrollPaymentPeriodFor(payment);
                  const paystubRow = payrollPaymentToPaystubRow(payment);
                  const paymentId = payrollPaymentId(payment);
                  const isSelected = paymentId && paymentId === payrollPaymentId(selectedPayrollPayment);
                  return (
                    <List.Item key={paymentId || payment.createdAt}>
                      <div className={`payroll-pay-staff-row payroll-paystub-record-row${isSelected ? " is-selected" : ""}`}>
                        <div className="payroll-pay-staff-person">
                          <Text strong>{firstText(payment.staffName, payment.userEmail, userEmail, "Staff member")}</Text>
                          <div><Text type="secondary">{firstText(payment.staffEmail, payment.userEmail, userEmail, "No email on record")}</Text></div>
                        </div>
                        <div className="payroll-pay-staff-metrics">
                          <span>
                            <strong>{shortDateText(period.start)} - {shortDateText(period.end)}</strong>
                            <small>Pay period</small>
                          </span>
                          <span>
                            <strong>{shortDateText(period.payDate)}</strong>
                            <small>Pay date</small>
                          </span>
                          <span>
                            <strong>{formatHours(paystubRow.approvedHours)}</strong>
                            <small>Approved hours</small>
                          </span>
                          <span>
                            <strong>{money(paystubRow.estimatedPay, payment.currency)}</strong>
                            <small>Net pay</small>
                          </span>
                        </div>
                        <Space wrap>
                          <Tag color={paymentStatusColor(paymentStatusText(payment))}>{readable(paymentStatusText(payment), "Status")}</Tag>
                          <Button
                            className="payroll-blue-button"
                            type={isSelected ? "primary" : "default"}
                            icon={<FileTextOutlined />}
                            loading={loadingPayrollPaymentDetail === paymentId}
                            onClick={() => openPayrollPaymentDetails(payment)}
                          >
                            Details
                          </Button>
                        </Space>
                      </div>
                    </List.Item>
                  );
                }}
              />
            ) : (
              <Empty image={Empty.PRESENTED_IMAGE_SIMPLE} description="No issued payroll payment records found." />
            )}
          </Card>
        </Space>
      ) : null}

      {activeTab === "documents" ? (
        <Card title={<Text strong style={{ color: OLIVEEB_COLORS.oliveebGreen }}>Payroll Documents</Text>}>
          <Empty
            image={Empty.PRESENTED_IMAGE_SIMPLE}
            description="W-2s and other payroll documents will appear here."
          >
            <Space>
              <Tag icon={<FileTextOutlined />} color="green">W-2s</Tag>
              <Tag icon={<FileTextOutlined />} color="blue">Other documents</Tag>
            </Space>
          </Empty>
        </Card>
      ) : null}

      {selectedPayrollPayment ? (() => {
        const payment = selectedPayrollPayment;
        const details = objectValue(payment.paymentDetails);
        const employer = objectValue(details.employer);
        const period = payrollPaymentPeriodFor(payment);
        const paystubRow = payrollPaymentToPaystubRow(payment);
        const documents = payrollDocumentsFor(payment);
        const ytdTotals = payrollPaymentYtdTotalsFor(payment);
        const staffName = firstText(profileNameFor(paystubRow.profile), user?.name, userEmail, "Employee");
        const staffEmail = firstText(profileEmailFor(paystubRow.profile), userEmail);
        const paystubTimesheets = paystubRow.timesheets || [];
        return (
          <Drawer
            className="payroll-staff-drawer payroll-paystub-drawer"
            extra={
              <Button
                className="payroll-blue-button"
                icon={<PrinterOutlined />}
                disabled={!paystubRow || loadingPayrollPaymentDetail === payrollPaymentId(payment)}
                onClick={printPaystub}
              >
                Print Paystub
              </Button>
            }
            open={Boolean(selectedPayrollPayment)}
            width={980}
            title={<Text strong className="payroll-section-title">Paystub Details</Text>}
            onClose={() => setSelectedPayrollPayment(null)}
            destroyOnClose
          >
            <Space direction="vertical" size={12} style={{ width: "100%" }}>
              <div className="payroll-paystub-summary">
                <span>
                  <small>Staff member</small>
                  <strong>{staffName}</strong>
                  <em>{staffEmail || "No email on record"}</em>
                </span>
                <span>
                  <small>Pay period</small>
                  <strong>{shortDateText(period.start)} - {shortDateText(period.end)}</strong>
                  <em>Pay date {shortDateText(period.payDate)}</em>
                </span>
                <span>
                  <small>Approved hours</small>
                  <strong>{formatHours(paystubRow.approvedHours)}</strong>
                  <em>{paystubTimesheets.length} approved timesheet{paystubTimesheets.length === 1 ? "" : "s"}</em>
                </span>
              </div>
              <PaystubDetailsCard
                payment={payment}
                row={paystubRow}
                period={{
                  start: shortDateText(period.start),
                  end: shortDateText(period.end),
                  payDate: shortDateText(period.payDate),
                }}
                employer={employer}
                ytdTotals={ytdTotals}
                staffName={staffName}
                staffEmail={staffEmail}
                staffPhone={profilePhoneFor(paystubRow.profile)}
                employeeAddress={paystubRow.employeeAddress}
                employerName={firstText(employer.name, payment.workspaceName, "Organization")}
                employerAddress={firstText(employer.address, payment.workspaceAddress)}
                employerContact={[employer.email, employer.phone].map((value) => String(value || "").trim()).filter(Boolean).join(" | ")}
                money={money}
                formatHours={formatHours}
                payRateDisplayParts={payRateDisplayParts}
                computedPayrollTaxLinesFor={computedPayrollTaxLinesFor}
                computedPayrollTaxAmountFor={computedPayrollTaxAmountFor}
                hasAnyPayRate={hasAnyPayRate}
                regularPayAmountFor={regularPayAmountFor}
                overtimePayAmountFor={overtimePayAmountFor}
                payrollItemAmount={payrollItemAmount}
              />

              <Card size="small" title={<Text strong style={{ color: OLIVEEB_COLORS.oliveebGreen }}>Documents</Text>}>
                {documents.length ? (
                  <Row gutter={[10, 10]}>
                    {documents.map((document, index) => {
                      const url = payrollDocumentUrl(document);
                      const name = payrollDocumentName(document, document.fallbackName || `Document ${index + 1}`);
                      return (
                        <Col xs={24} md={8} key={`${name}-${index}`}>
                          <Card size="small" style={{ borderRadius: 8 }}>
                            <div
                              style={{
                                border: `1px solid ${OLIVEEB_COLORS.oliveebBlue}`,
                                borderRadius: 8,
                                minHeight: 92,
                                display: "flex",
                                alignItems: "center",
                                justifyContent: "center",
                                background: "#f4f9ff",
                                overflow: "hidden",
                              }}
                            >
                              {url && isPayrollImageDocument(document, url) ? (
                                <img src={url} alt={name} style={{ width: "100%", height: 92, objectFit: "cover" }} />
                              ) : (
                                <FileTextOutlined style={{ color: OLIVEEB_COLORS.oliveebBlue, fontSize: 28 }} />
                              )}
                            </div>
                            <Text strong style={{ display: "block", marginTop: 8 }}>{document.kind || "Document"}</Text>
                            {url ? (
                              <a href={url} target="_blank" rel="noreferrer">{name}</a>
                            ) : (
                              <Text type="secondary">{name}</Text>
                            )}
                          </Card>
                        </Col>
                      );
                    })}
                  </Row>
                ) : (
                  <Empty image={Empty.PRESENTED_IMAGE_SIMPLE} description="No documents attached." />
                )}
              </Card>
            </Space>
          </Drawer>
        );
      })() : null}

      <Drawer
        title="Create Personal Workspace"
        open={createWorkspaceOpen}
        onClose={() => setCreateWorkspaceOpen(false)}
        width={520}
        destroyOnClose
      >
        <Alert
          type="info"
          showIcon
          message="Personal workspace only"
          description="This creates a personal workspace for your payroll payments, incoming payment methods, transactions, and payouts."
          style={{ marginBottom: 16 }}
        />
        <Form
          form={createWorkspaceForm}
          layout="vertical"
          requiredMark={false}
          onFinish={createPersonalWorkspace}
        >
          <Form.Item
            name="name"
            label="Workspace name"
            rules={[
              { required: true, message: "Enter a workspace name." },
              {
                validator: (_, value) =>
                  firstText(value)
                    ? Promise.resolve()
                    : Promise.reject(new Error("Enter a workspace name.")),
              },
            ]}
          >
            <Input
              placeholder="My payment workspace"
              prefix={<BankOutlined style={{ color: OLIVEEB_COLORS.oliveebBlue }} />}
            />
          </Form.Item>
          <Form.Item name="description" label="Description">
            <Input.TextArea rows={3} placeholder="Optional description" />
          </Form.Item>
          <Space style={{ width: "100%", justifyContent: "flex-end" }}>
            <Button onClick={() => setCreateWorkspaceOpen(false)} disabled={creatingWorkspace}>
              Cancel
            </Button>
            <Button type="primary" htmlType="submit" loading={creatingWorkspace} icon={<PlusOutlined />}>
              Create and Link
            </Button>
          </Space>
        </Form>
      </Drawer>
    </CareShell>
  );
};

export default MyPaymentsPage;
